import { HistoryOutlined, UserAddOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Flex, Modal, message } from 'antd';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Schemas } from '@api-client/generated/types';
import { AccessDenied, AttachEmployeeModal, CompanyTeamRoster } from '@entities';
import { MembershipJournalDrawer } from '@entities/MembershipJournal';
import { useAccount } from '@hooks';
import { LAST_COMPANY_USER_ERROR, useCompanyTeam } from '@hooks-api';
import { lessRoundedTags } from '@theme';
import { getApiErrorMessage } from '@utils';

import * as S from './styled';

type Membership = Schemas.AdminMembershipRecordDto;

const CompanyTeam: FC = () => {
  const { companyId = '' } = useParams<{ companyId: string }>();
  const { role } = useAccount();

  const {
    members,
    isLoading,
    attach,
    detach,
    isAttaching,
    isDetaching,
    refetch,
  } = useCompanyTeam(companyId);

  const [isAttachOpen, setIsAttachOpen] = useState(false);
  const [isJournalOpen, setIsJournalOpen] = useState(false);

  // Defense in depth: the router guards on the parent route's roles, not this
  // matched child's, so the page re-checks the admin role itself.
  if (role !== 'admin') return <AccessDenied />;

  const handleAttach = async (
    userId: string,
    accessRights: Schemas.AccessRights
  ) => {
    try {
      await attach(userId, accessRights);
      message.success('Team member added');
      setIsAttachOpen(false);
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to add team member'));
    }
  };

  const handleDetach = (member: Membership) => {
    Modal.confirm({
      title: 'Remove team member?',
      content: `${member.firstName} ${member.lastName} will lose access to this company.`,
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await detach(member.userId);
          message.success('Team member removed');
        } catch (error) {
          if (getApiErrorMessage(error, '') === LAST_COMPANY_USER_ERROR) {
            // Snapshot was stale; the roster has been refetched by the hook.
            void refetch();
            message.error(
              'This is the last member of the company and can’t be removed'
            );
            return;
          }
          message.error(
            getApiErrorMessage(error, 'Failed to remove team member')
          );
        }
      },
    });
  };

  return (
    <ConfigProvider theme={lessRoundedTags}>
      <S.Wrapper gap={24} vertical>
        <S.Header align="center" justify="space-between" gap={16}>
          <S.Wrapper vertical gap={2}>
            <S.Title>Team</S.Title>
            <S.Subtitle>
              Employees with access to this company’s portal
            </S.Subtitle>
          </S.Wrapper>
          <Flex gap={12}>
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setIsJournalOpen(true)}
            >
              Access history
            </Button>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => setIsAttachOpen(true)}
            >
              Add member
            </Button>
          </Flex>
        </S.Header>

        <CompanyTeamRoster
          members={members}
          isLoading={isLoading}
          isDetaching={isDetaching}
          onDetach={handleDetach}
        />

        <AttachEmployeeModal
          open={isAttachOpen}
          attachedUserIds={members.map((member) => member.userId)}
          isAttaching={isAttaching}
          onClose={() => setIsAttachOpen(false)}
          onAttach={handleAttach}
        />

        <MembershipJournalDrawer
          open={isJournalOpen}
          companyId={companyId}
          companyName={members[0]?.companyName}
          knownMembers={members}
          onClose={() => setIsJournalOpen(false)}
        />
      </S.Wrapper>
    </ConfigProvider>
  );
};

export default CompanyTeam;
